// Barba page transitions.
//
// Wrapper: <body data-barba="wrapper">. Container: .site-wrapper
// (header.php) — the WHOLE page (header + main + footer) is swapped and
// animated as one card on navigation. Only elements outside .site-wrapper
// (skip link, future cursor/modal overlays) persist.
//
// Emits on the bus: 'page:once', 'page:leave', 'page:enter' (with barba's
// data object) so persistent components can react without being torn down.
//
// Disabled entirely for logged-in users (admin bar breaks under PJAX) and
// for reduced-motion users. Links can opt out with data-no-barba.
import barba from '@barba/core';
import prefetch from '@barba/prefetch';
import { gsap, ScrollTrigger } from './gsap';
import { bus } from './bus';
import { mountBlocks, unmountBlocks } from './blocks';
import { scrollToTop, stopScroll, startScroll, reducedMotion } from './scroll';

const DURATION = 1.4;
const EASE = 'power3.inOut';

// When the incoming page's block intros begin, as a fraction of the
// transition — by 80% the slide has visually settled (power4.inOut tail),
// so the intros overlap the last stretch instead of waiting for it.
const BLOCKS_START_AT = 0.5;

// Head elements kept in sync on navigation (Barba only swaps the container
// and <title> by itself).
const HEAD_SYNC = ['meta[name="description"]', 'meta[property^="og:"]', 'link[rel="canonical"]'];

// Full page loads for anything WordPress-y or explicitly opted out.
function preventBarba({ el }) {
	if (!el || !el.href) return false;

	return el.closest('[data-no-barba]') !== null || /wp-admin|wp-login|\.(pdf|zip|jpg|png|webp)$/i.test(el.href);
}

function syncHead(nextHtml) {
	const nextDoc = new DOMParser().parseFromString(nextHtml, 'text/html');

	HEAD_SYNC.forEach((selector) => {
		const current = document.head.querySelectorAll(selector);
		const incoming = nextDoc.head.querySelectorAll(selector);

		current.forEach((node) => node.remove());
		incoming.forEach((node) => document.head.appendChild(node.cloneNode(true)));
	});
}

export function initTransitions() {
	const barbaDisabled = document.body.classList.contains('logged-in') || reducedMotion();

	if (barbaDisabled) {
		// No PJAX: mount blocks for the initial (and only) page load.
		mountBlocks(document).then(() => bus.emit('page:once'));
		return;
	}

	barba.use(prefetch);

	// Starter for the incoming page's blocks — mounting happens during the
	// transition (chunks load while the page slides), but enter() intros
	// play only after it settles, so they aren't wasted offscreen.
	let startBlocks = null;

	barba.init({
		timeout: 5000,
		prevent: preventBarba,
		transitions: [
			{
				// Stacked-deck transition: the old page dims toward the black
				// body backdrop, scales down and drifts up, while the new page
				// slides in from the bottom above it.
				name: 'stack',
				sync: true, // leave + enter run together; both containers coexist

				// Initial page load.
				async once({ next }) {
					await mountBlocks(next.container);
					bus.emit('page:once', { next });
				},

				// Pin the incoming page as a fixed overlay parked below the
				// viewport BEFORE anything animates — keeping it out of the
				// document flow (no height jump, no scrollbar flash). The
				// container is the whole page, so it slides in header and all.
				beforeEnter({ next }) {
					gsap.set(next.container, {
						position: 'fixed',
						inset: 0,
						height: '100vh',
						overflow: 'hidden',
						zIndex: 40, // matches $z-overlay — above page content, below skip link/modals
						y: '100vh',
					});
				},

				async leave(data) {
					bus.emit('page:leave', data);
					stopScroll();
				},

				async enter(data) {
					const { current, next } = data;

					syncHead(next.html);
					startBlocks = await mountBlocks(next.container, { defer: true });

					const tl = gsap.timeline({
						defaults: { duration: DURATION, ease: EASE },
					});

					// Old page: dim into the backdrop, shrink, drift up.
					tl.to(current.container, { opacity: 0.15, transformOrigin: 'top', y: '-20vh' }, 0);
					// New page: slide up from the bottom, covering it.
					tl.to(next.container, { y: 0 }, 0);

					// Kick the intros just before the slide settles. after()
					// keeps a guarded fallback call in case the timeline is
					// interrupted before this fires.
					tl.call(
						() => {
							if (startBlocks) {
								startBlocks();
								startBlocks = null;
							}
						},
						null,
						DURATION * BLOCKS_START_AT,
					);

					await tl;
				},

				// Runs when both hooks settle; barba removes the old container
				// right after this.
				async after(data) {
					unmountBlocks(data.current.container);
					data.current.container.style.display = 'none';
					scrollToTop(true);
					// Release the overlay back into normal document flow. Only
					// the props beforeEnter/the timeline set — clearProps 'all'
					// would also wipe the --header-h custom property that
					// blocks/header.js maintains on the container, collapsing
					// the hero's header pull-up to its CSS fallback.
					gsap.set(data.next.container, {
						clearProps: 'position,inset,height,overflow,zIndex,transform',
					});

					// Now that the page is visible, in flow and scrolled to
					// top, play the blocks' intros — measurements are exact.
					if (startBlocks) {
						startBlocks();
						startBlocks = null;
					}

					ScrollTrigger.refresh();
					startScroll();
					bus.emit('page:enter', data);
				},
			},
		],
	});
}
